import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { includes, last, size } from 'lodash/fp';
import OptionCursorFollower from './OptionCursorFollower';

const S = {
  Wrapper: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${({ active }) => (active ? 100 : -100)};
    width: 100%;
    height: 100%;
    cursor: none;
  `,
};

const OptionField = ({
  bindHover,
  canvasWrapper,
  viewport,
  viewportWidth,
  step,
  type,
  push,
  form,
  optionType,
  setOptionType,
  setVisible,
}) => {
  const [isShown, setIsShown] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const active = useMemo(() => step === 3 && includes(type, ['SINGLE_SELECTION', 'MULTIPLE_SELECTION']), [step, type]);

  const handleMouseMove = useCallback((event) => {
    setPosition({ x: event.clientX, y: event.clientY });
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsShown(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsShown(false);
  }, []);

  const handleClick = useCallback((event) => {
    const canvasWrapperRect = canvasWrapper.current.getBoundingClientRect();
    const viewportRect = viewport.current.getBoundingClientRect();

    const relativeX = event.clientX - viewportRect.left + viewport.current.scrollLeft + 2;
    const relativeY = event.clientY - viewportRect.top + viewport.current.scrollTop + 2;

    const x = (relativeX / canvasWrapperRect.width) * 100;
    const y = (relativeY / canvasWrapperRect.height) * 100;

    setVisible(true);

    setOptionType('SELECTION');

    push({
      type: optionType,
      size: optionType === 'TEXT'
        ? { width: 25 }
        : null,
      number: size(last(form.values.questions).options) + 1,
      content: '',
      position: { x, y },
    });
  }, [canvasWrapper, viewport, setVisible, setOptionType, push, optionType, form.values.questions]);

  return (
    <S.Wrapper
      active={active}
      type={type}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {isShown && (
        <OptionCursorFollower
          bindHover={bindHover}
          position={position}
          questions={form.values.questions}
          type={type}
          optionType={optionType}
          viewportWidth={viewportWidth}
        />
      )}
    </S.Wrapper>
  );
};

OptionField.propTypes = {
  bindHover: PropTypes.shape({
    onMouseEnter: PropTypes.func.isRequired,
    onMouseLeave: PropTypes.func.isRequired,
  }).isRequired,
  canvasWrapper: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLElement),
  }).isRequired,
  viewport: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLElement),
  }).isRequired,
  viewportWidth: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  type: PropTypes.oneOf(['SINGLE_SELECTION', 'MULTIPLE_SELECTION', 'DESCRIPTIVE', 'SIGNATURE']),
  push: PropTypes.func.isRequired,
  form: PropTypes.shape({
    values: PropTypes.shape({
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          options: PropTypes.arrayOf(
            PropTypes.shape({
              number: PropTypes.number.isRequired,
              content: PropTypes.string.isRequired,
              position: PropTypes.shape({
                x: PropTypes.number.isRequired,
                y: PropTypes.number.isRequired,
              }),
            }),
          ),
        }),
      ).isRequired,
    }).isRequired,
  }).isRequired,
  optionType: PropTypes.oneOf(['SELECTION', 'TEXT']).isRequired,
  setOptionType: PropTypes.func.isRequired,
  setVisible: PropTypes.func.isRequired,
};

OptionField.defaultProps = {
  type: null,
};

export default OptionField;
